/**
 * @fileoverview Criar conta (30/10/2026, etapa 4 dos planos): nome, e-mail e
 * senha, sem cartão. Depois do envio, "confira seu e-mail" (com o reenvio). A
 * resposta é a mesma exista ou não a conta: a tela não diz quem tem cadastro.
 * Com o cadastro fechado no servidor (SIGNUP_ENABLED), avisa e volta ao login.
 * A prova de pessoa roda invisível (useHumanCheck). Quem esbarra no limite vê em
 * quanto tempo tentar de novo e o contato da Sttelar: nunca fica sem saída.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Eye, EyeOff } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import AuthLayout from "../../components/AuthLayout";
import useLanguage from "../../hooks/useLanguage";
import { useAuthFormStyles } from "../../components/AuthLayout/formStyles";
import { useHumanCheck } from "../../components/AuthLayout/useHumanCheck";
import TermsCheckbox from "../../components/AuthLayout/TermsCheckbox";
import { EMAIL_CONFIRMED_KEY, EMAIL_RE, errorCode, errorText, saveSignupBrowser } from "../../components/AuthLayout/authHelpers";
import { PAID_PLAN_NAMES, isPaidPlan } from "../../utils/billingPlans";
import { sttelarWhatsAppUrl } from "../../utils/sttelarContact";

const LATE_TOKEN_WAIT_MS = 8000;

const FIELD_OF_ERROR = {
  ERR_SIGNUP_INVALID_NAME: "name",
  ERR_INVALID_EMAIL: "email",
  ERR_EMAIL_DOMAIN_NOT_FOUND: "email",
  ERR_EMAIL_DISPOSABLE: "email",
  ERR_TERMS_NOT_ACCEPTED: "terms",
  ERR_PASSWORD_TOO_SHORT: "password",
  ERR_PASSWORD_TOO_LONG: "password",
};

const Signup = () => {
  useLanguage(); // trocou o idioma no globo: redesenha já traduzida
  const classes = useAuthFormStyles();
  const location = useLocation();
  // O "Criar conta grátis" do Basic ou do Pro no site traz o plano (?plan=pro): a contratação abre no fim do cadastro.
  const chosenPlan = useMemo(() => {
    const plan = new URLSearchParams(location.search).get("plan");
    return isPaidPlan(plan) ? plan : null;
  }, [location.search]);
  const [status, setStatus] = useState("loading");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [resent, setResent] = useState(false);
  // A contratação do plano escolhido só abre no fim com a cobrança ligada (o servidor diz).
  const [billingOn, setBillingOn] = useState(false);
  const [humanCheckSiteKey, setHumanCheckSiteKey] = useState(null);
  // Esbarrou no limite: em quantos minutos pode tentar de novo.
  const [limitMinutes, setLimitMinutes] = useState(null);
  const humanCheck = useHumanCheck(status === "open" && !sentTo ? humanCheckSiteKey : null);

  useEffect(() => {
    let alive = true;
    openApi
      .get("/auth/signup")
      .then(({ data }) => {
        if (!alive) return;
        setStatus(data?.enabled ? "open" : "closed");
        setBillingOn(!!data?.billing);
        setHumanCheckSiteKey(data?.humanCheckSiteKey || null);
      })
      // Sem a resposta, mostra o formulário: se o cadastro estiver fechado, o envio diz.
      .catch(() => alive && setStatus("open"));
    return () => {
      alive = false;
    };
  }, []);

  // Confirmou em outra aba e entrou por lá: esta, que esperava o e-mail, segue também
  // (recarregar uma tela de entrada apagaria a sessão recém-aberta).
  useEffect(() => {
    if (!sentTo) return undefined;
    const onStorage = (e) => {
      if (e.key === EMAIL_CONFIRMED_KEY && localStorage.getItem("token")) window.location.assign("/");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [sentTo]);

  const change = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = i18n.t("auth.signup.errors.name");
    if (!EMAIL_RE.test(form.email.trim())) next.email = i18n.t("login.errors.emailInvalid");
    if (form.password.length < 8) next.password = i18n.t("backendErrors.ERR_PASSWORD_TOO_SHORT");
    if (!acceptedTerms) next.terms = i18n.t("backendErrors.ERR_TERMS_NOT_ACCEPTED");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const postSignup = (humanToken) => openApi.post("/auth/signup", { name: form.name.trim(), email: form.email.trim(), password: form.password, acceptedTerms, ...(chosenPlan ? { plan: chosenPlan } : {}), ...(humanToken ? { humanToken } : {}) });

  const showLimit = (err) => setLimitMinutes(Math.ceil((err?.response?.data?.retryAfterSeconds || 3600) / 60));

  const submit = async (e) => {
    e.preventDefault();
    if (sending || !validate()) return;
    setSending(true);
    setLimitMinutes(null);
    try {
      const humanToken = await humanCheck.takeToken();
      const { data } = await postSignup(humanToken).catch(async (err) => {
        // Barrada sem a prova: se ela ainda chegar, vale repetir agora, e não daqui a uma hora.
        const lateToken = !humanToken && errorCode(err) === "ERR_TOO_MANY_AUTH_ATTEMPTS" ? await humanCheck.takeToken(LATE_TOKEN_WAIT_MS) : null;
        if (!lateToken) throw err;
        return postSignup(lateToken);
      });
      saveSignupBrowser(data?.browserProof);
      setSentTo(form.email.trim());
    } catch (err) {
      humanCheck.reset();
      const code = errorCode(err);
      if (code === "ERR_SIGNUP_DISABLED") setStatus("closed");
      else if (code === "ERR_TOO_MANY_AUTH_ATTEMPTS") showLimit(err);
      else if (FIELD_OF_ERROR[code]) setErrors({ [FIELD_OF_ERROR[code]]: errorText(err) });
      else toastError(err);
    } finally {
      setSending(false);
    }
  };

  const resend = async () => {
    setSending(true);
    setLimitMinutes(null);
    try {
      await openApi.post("/auth/verify-email/resend", { email: sentTo });
      setResent(true);
    } catch (err) {
      if (errorCode(err) === "ERR_TOO_MANY_AUTH_ATTEMPTS") showLimit(err);
      else toastError(err);
    } finally {
      setSending(false);
    }
  };

  const limitNotice = limitMinutes && (
    <div className={classes.notice} role="alert" style={{ marginBottom: 16 }}>
      {i18n.t("auth.signup.limited", { minutes: limitMinutes })}{" "}
      <a className={classes.link} href={sttelarWhatsAppUrl(i18n.t("auth.signup.closedMessage"))} target="_blank" rel="noopener noreferrer">
        {i18n.t("auth.signup.talkToSales")}
      </a>
    </div>
  );

  if (status === "loading") return <AuthLayout />;

  if (status === "closed") {
    return (
      <AuthLayout>
        <div className={classes.view}>
          <h1 className={classes.title}>{i18n.t("auth.signup.closedTitle")}</h1>
          <p className={classes.subtitle}>{i18n.t("auth.signup.closedText")}</p>
          <a className={`${classes.button} ${classes.linkButton}`} href={sttelarWhatsAppUrl(i18n.t("auth.signup.closedMessage"))} target="_blank" rel="noopener noreferrer">
            {i18n.t("auth.signup.talkToSales")}
          </a>
          <div className={classes.alt}>
            <RouterLink to="/login" className={classes.link}>{i18n.t("auth.backToLogin")}</RouterLink>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (sentTo) {
    return (
      <AuthLayout>
        <div className={classes.view}>
          <h1 className={classes.title}>{i18n.t("auth.checkEmailTitle")}</h1>
          <div className={classes.notice} role="status">{i18n.t("auth.signup.sent", { email: sentTo })}</div>
          {limitNotice}
          <p className={classes.subtitle} style={{ marginBottom: 12 }}>{resent ? i18n.t("auth.resent") : i18n.t("auth.notArrived")}</p>
          {!resent && (
            <button type="button" className={classes.secondary} onClick={resend} disabled={sending}>{i18n.t("auth.resend")}</button>
          )}
          <div className={classes.alt} style={{ columnGap: 20 }}>
            <button type="button" className={classes.link} onClick={() => { setSentTo(null); setResent(false); setLimitMinutes(null); }}>{i18n.t("auth.signup.fixEmail")}</button>
            <RouterLink to="/login" className={classes.link}>{i18n.t("auth.backToLogin")}</RouterLink>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form className={classes.view} noValidate onSubmit={submit}>
        <h1 className={classes.title}>{i18n.t("auth.signup.title")}</h1>
        <p className={classes.subtitle}>{i18n.t("auth.signup.subtitle")}</p>
        {chosenPlan && (
          <div className={classes.notice} role="status">
            {billingOn ? (
              i18n.t("auth.signup.planChosen", { plan: PAID_PLAN_NAMES[chosenPlan] })
            ) : (
              // Sem a cobrança ligada, a conta começa no Starter e o plano escolhido é com o comercial.
              <>
                {i18n.t("auth.signup.planLater", { plan: PAID_PLAN_NAMES[chosenPlan] })}{" "}
                <a className={classes.link} href={sttelarWhatsAppUrl(i18n.t("auth.signup.planMessage", { plan: PAID_PLAN_NAMES[chosenPlan] }))} target="_blank" rel="noopener noreferrer">
                  {i18n.t("auth.signup.talkToSales")}
                </a>
              </>
            )}
          </div>
        )}

        <div className={classes.field}>
          <label className={classes.label} htmlFor="signup-name">{i18n.t("auth.fields.name")}</label>
          <input id="signup-name" name="name" className={classes.input} value={form.name} onChange={change} autoComplete="name" autoFocus
            aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? "signup-name-error" : undefined} />
          {errors.name && <div id="signup-name-error" className={classes.error}>{errors.name}</div>}
        </div>

        <div className={classes.field}>
          <label className={classes.label} htmlFor="signup-email">{i18n.t("auth.fields.email")}</label>
          <input id="signup-email" name="email" type="email" className={classes.input} value={form.email} onChange={change} autoComplete="email"
            aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? "signup-email-error" : undefined} />
          {errors.email && <div id="signup-email-error" className={classes.error}>{errors.email}</div>}
        </div>

        <div className={classes.field}>
          <label className={classes.label} htmlFor="signup-password">{i18n.t("auth.fields.password")}</label>
          <div className={classes.inputWrap}>
            <input id="signup-password" name="password" type={showPassword ? "text" : "password"} className={`${classes.input} ${classes.inputWithButton}`}
              value={form.password} onChange={change} autoComplete="new-password"
              aria-invalid={Boolean(errors.password)} aria-describedby="signup-password-hint" />
            <button type="button" className={classes.eye} onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? i18n.t("auth.hidePassword") : i18n.t("auth.showPassword")}>
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password
            ? <div id="signup-password-hint" className={classes.error}>{errors.password}</div>
            : <div id="signup-password-hint" className={classes.hint}>{i18n.t("auth.passwordHint")}</div>}
        </div>

        <TermsCheckbox
          id="signup-terms"
          checked={acceptedTerms}
          onChange={(value) => {
            setAcceptedTerms(value);
            if (errors.terms) setErrors({ ...errors, terms: "" });
          }}
          error={errors.terms}
        />

        <div ref={humanCheck.containerRef} className={classes.humanCheck} />

        {limitNotice}

        <button type="submit" className={classes.button} disabled={sending} style={{ marginTop: 8 }}>
          {sending && <CircularProgress size={18} className={classes.spinner} />}
          {i18n.t("auth.signup.submit")}
        </button>

        <div className={classes.alt}>
          <span>{i18n.t("auth.signup.haveAccount")}</span>
          <RouterLink to="/login" className={classes.link}>{i18n.t("auth.signup.login")}</RouterLink>
        </div>
      </form>
    </AuthLayout>
  );
};

export default Signup;
